import React, { useState, useMemo, useCallback } from 'react';
import { useLocation, Link } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  Typography,
  Collapse,
  CircularProgress,
} from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { TEXT_COLORS, BG_COLORS, BORDER_COLORS, SCROLLBAR_COLORS } from '../../utils/colors';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';

// Адаптивная ширина сайдбара
const drawerWidth = {
  xs: 280, // Мобильные устройства
  sm: 300, // Планшеты
  md: 320, // Десктоп
  lg: 340, // Большие экраны
};

// Статичные стили вынесем наружу - создаются один раз
const drawerPaperStyles = {
  boxSizing: 'border-box',
  background: 'rgba(0, 0, 0, 0.49)',
  backdropFilter: 'blur(10px)',
  borderRadius: '40px',
  boxShadow: '-10px -20px 20px 0px rgba(123, 186, 201, 0.1), 15px 30px 30px 0px rgba(123, 186, 201, 0.08)',
  border: 'none',
  top: {
    xs: '84px', // Для мобильных (80px + 4px)
    sm: '116px' // Для десктопа (112px + 4px)
  },
  height: {
    xs: 'calc(100dvh - 200px)', // Для мобильных (dvh для iOS Safari)
    sm: 'calc(100dvh - 200px)' // Для десктопа — та же линия низа, что у GlassContainer
  },
  overflow: 'hidden',
  left: '40px',
  transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '-10px -20px 25px 0px rgba(123, 186, 201, 0.15), 15px 30px 35px 0px rgba(123, 186, 201, 0.12)',
  },
} as const;

const menuSectionStyles = {
  pt: '20px',
  pb: '40px',
} as const;

// Стили для фиксированной секции с нижней линией
const fixedSectionStyles = {
  position: 'relative',
  // Полная линия (внутри padding области)
  '&::before': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: { xs: '20px', sm: '25px', md: '30px', lg: '35px' },
    right: { xs: '20px', sm: '25px', md: '30px', lg: '35px' },
    height: '1px',
    background: BORDER_COLORS.LIGHTER,
  },
  // 60px анимированная линия по центру
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    width: '60px',
    height: '1px',
    background: `linear-gradient(90deg, transparent, ${TEXT_COLORS.DISABLED}, transparent)`,
    animation: 'shimmer 2s infinite',
  },
  '@keyframes shimmer': {
    '0%': { opacity: 0.5 },
    '50%': { opacity: 1 },
    '100%': { opacity: 0.5 },
  },
} as const;

export interface SidebarItem {
  id: string;
  text: string;
  icon?: React.ReactElement;
  prefixElement?: React.ReactNode; // Colored prefix before text
  path?: string;
  subItems?: SidebarItem[];
  onClick?: () => void;
  source?: string; // для wiki специальной логики
  accentColor?: string; // Цвет акцента для выделения пункта
}

export interface SidebarSection {
  id: string;
  title?: string;
  items?: SidebarItem[];
  customContent?: React.ReactNode;
  withMenuSection?: boolean; // применять ли menuSectionStyles
  fixed?: boolean; // секция фиксирована (не скроллится)
}

export interface UniversalSidebarProps {
  sections: SidebarSection[];
  mobileOpen: boolean;
  onDrawerToggle: () => void;
  loading?: boolean;
  /**
   * localStorage key for persisting expanded/collapsed state.
   * If not provided, state is NOT persisted (resets on page leave).
   * Example: "wikiSidebarExpanded", "adminSidebarExpanded"
   */
  storageKey?: string;
  /**
   * If true, only shows mobile drawer (no permanent desktop sidebar).
   * Use for public pages where sidebar is only accessible via burger menu.
   */
  mobileOnly?: boolean;
  /**
   * Свернуть постоянный сайдбар на десктопе, отдав место контенту. Мобильная шторка
   * не затрагивается. Опционально: зоны, которые проп не передают, ведут себя как раньше.
   * Контенту в паре с этим нужен `MainContent fullWidth`.
   */
  collapsed?: boolean;
}

/** Въезд пункта меню лесенкой: каждый следующий — на 0.1 с позже предыдущего. */
const slideInMenu = (index: number) => ({
  animation: `slideInMenu 0.4s ease-out ${index * 0.1}s backwards`,
  '@keyframes slideInMenu': {
    '0%': {
      opacity: 0,
      transform: 'translateX(-20px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateX(0)',
    },
  },
});

const UniversalSidebar: React.FC<UniversalSidebarProps> = ({
  sections,
  mobileOpen,
  onDrawerToggle,
  loading = false,
  storageKey,
  mobileOnly = false,
  collapsed = false,
}) => {
  const location = useLocation();

  // Initialize expandedItems from localStorage (only if storageKey provided) or empty set
  const [expandedItems, setExpandedItems] = useState<Set<string>>(() => {
    if (!storageKey) return new Set();
    try {
      const saved = localStorage.getItem(storageKey);
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      return new Set();
    }
  });

  const toggleExpanded = useCallback((key: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedItems(newExpanded);

    // Save to localStorage only if storageKey is provided
    if (storageKey) {
      try {
        localStorage.setItem(storageKey, JSON.stringify(Array.from(newExpanded)));
      } catch (error) {
        console.warn('Failed to save sidebar state to localStorage:', error);
      }
    }
  }, [expandedItems, storageKey]);

  // Function to check if an item is active based on current path
  const isItemActive = useCallback((path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  }, [location.pathname]);

  // Мемоизированные стили для иконок
  const getIconStyles = useMemo(() => (isActive: boolean, accentColor?: string) => ({
    fontSize: {
      xs: 18,
      sm: 19,
      md: 20,
      lg: 22
    },
    color: accentColor
      ? accentColor
      : isActive ? 'white' : TEXT_COLORS.MUTED,
    transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
    filter: isActive ? `drop-shadow(0 0 8px ${accentColor || TEXT_COLORS.DISABLED})` : 'none',
    transform: isActive ? 'scale(1.1)' : 'scale(1)',
    animation: isActive ? 'pulse 2s infinite' : 'none',
    '@keyframes pulse': {
      '0%': { transform: 'scale(1.1)' },
      '50%': { transform: 'scale(1.2)' },
      '100%': { transform: 'scale(1.1)' },
    },
  }), []);

  // Мемоизированные стили для текста
  const getTextStyles = useMemo(() => (isActive: boolean) => ({
    fontWeight: isActive ? 600 : 500,
    fontSize: {
      xs: '14px',
      sm: '15px',
      md: '16px',
      lg: '17px'
    },
    background: isActive
      ? 'linear-gradient(45deg, #ffffff 0%, #e0e0e0 50%, #ffffff 100%)'
      : 'linear-gradient(to right, #ffffff 70.5%, #5c5b5b)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    letterSpacing: isActive ? '1.2px' : '1px',
    textTransform: 'capitalize',
    lineHeight: '21px',
    whiteSpace: 'nowrap',
    transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
    textShadow: isActive ? `0 0 10px ${SCROLLBAR_COLORS.THUMB}` : 'none',
    transform: isActive ? 'translateX(2px)' : 'translateX(0)'
  }), []);

  // Мемоизированные стили для ListItemButton
  const getButtonStyles = useMemo(() => (isActive: boolean, index: number) => ({
    p: '8px', // Уменьшенный padding для компактности
    display: 'flex',
    alignItems: 'center',
    minHeight: 'auto',
    borderRadius: isActive ? '15px' : 0,
    background: isActive ? BG_COLORS.HOVER : 'transparent',
    border: isActive ? `1px solid ${BORDER_COLORS.STRONG}` : '1px solid transparent',
    transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    transform: isActive ? 'translateX(5px)' : 'translateX(0)',
    position: 'relative',
    overflow: 'hidden',
    // Material UI Ripple effect
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '50%',
      left: '50%',
      width: '0',
      height: '0',
      background: BORDER_COLORS.STRONG,
      borderRadius: '50%',
      transform: 'translate(-50%, -50%)',
      transition: 'width 0.6s ease, height 0.6s ease',
    },
    '&:active::after': {
      width: '200px',
      height: '200px',
    },
    '&:hover': {
      background: isActive ? BG_COLORS.SELECTED : BG_COLORS.GLASS_LIGHT,
      transform: 'translateX(8px) scale(1.02)',
      borderRadius: '15px',
      border: `1px solid ${BORDER_COLORS.FOCUS}`,
      boxShadow: `0 4px 20px ${BG_COLORS.GLASS_LIGHT}, 0 0 30px ${BG_COLORS.GLASS_DARK}`,
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `linear-gradient(45deg, transparent 30%, ${BG_COLORS.GLASS_LIGHT} 50%, transparent 70%)`,
        transform: 'translateX(-100%)',
        animation: 'shine 0.6s ease-in-out',
      },
    },
    '&:active': {
      transform: 'translateX(3px) scale(0.98)',
    },
    '@keyframes shine': {
      '0%': { transform: 'translateX(-100%)', opacity: 0 },
      '50%': { opacity: 1 },
      '100%': { transform: 'translateX(100%)', opacity: 0 },
    },
  }), []);

  // Рендеринг элементов меню
  const renderMenuItem = useCallback((item: SidebarItem, index: number, level: number = 0) => {
    const hasSubItems = item.subItems && item.subItems.length > 0;
    const isActive = item.path ? isItemActive(item.path) : false;
    const itemKey = `item-${item.id}`;
    const accentColor = item.accentColor;

    return (
      <React.Fragment key={item.id}>
        <ListItem
          disablePadding
          sx={{
            mb: level > 0 ? '3px' : '6px', // Еще более компактные отступы
            ...slideInMenu(index),
          }}
        >
          <ListItemButton
            {...(item.path && !hasSubItems ? {
              component: Link,
              to: item.path,
            } : {})}
            onClick={(e: React.MouseEvent) => {
              if (hasSubItems) {
                toggleExpanded(itemKey);
              } else if (item.path) {
                // Link handles navigation, but close mobile drawer
                if (window.innerWidth < 870) {
                  onDrawerToggle();
                }
              } else if (item.onClick) {
                item.onClick();
              }
            }}
            sx={{
              ...getButtonStyles(isActive, index),
              ml: level * 1, // Уменьшенный отступ для вложенных элементов
              textDecoration: 'none',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', zIndex: 2, position: 'relative', width: '100%' }}>
              {item.icon && (
                <Box sx={{
                  width: { xs: 18, sm: 19, md: 20, lg: 22 },
                  height: { xs: 18, sm: 19, md: 20, lg: 22 },
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '30px',
                    height: '30px',
                    borderRadius: '50%',
                    background: isActive ? `radial-gradient(circle, ${BG_COLORS.SELECTED} 0%, transparent 70%)` : 'transparent',
                    transition: 'all 0.3s ease',
                    zIndex: -1,
                  }
                }}>
                  {React.cloneElement(item.icon as React.ReactElement<any>, {
                    sx: getIconStyles(isActive, accentColor)
                  })}
                </Box>
              )}
              {item.prefixElement}
              <Typography sx={{ ...getTextStyles(isActive), flex: 1 }}>
                {item.text}
              </Typography>
              {hasSubItems && (
                <Box sx={{
                  color: TEXT_COLORS.MUTED,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '24px',
                  height: '24px',
                }}>
                  {expandedItems.has(itemKey) ? <ExpandLess sx={{ fontSize: '20px' }} /> : <ExpandMore sx={{ fontSize: '20px' }} />}
                </Box>
              )}
            </Box>
          </ListItemButton>
        </ListItem>

        {/* Подэлементы. Обёртка повторяет въезд своего пункта с той же задержкой: подпункты
            раскрытой группы (раскрытие помнится между заходами) иначе считали задержку от нуля
            и стояли на месте раньше всего меню, пока остальные пункты только выезжали. Обёртка
            монтируется вместе с пунктом, так что при раскрытии кликом её анимация уже отыграла —
            подпункты раскрываются сразу, как раньше. */}
        {hasSubItems && (
          <Box sx={slideInMenu(index)}>
            <Collapse in={expandedItems.has(itemKey)} timeout="auto" unmountOnExit>
              <List sx={{ pl: 1, mt: 0 }}> {/* Уменьшенные отступы для компактности */}
                {item.subItems!.map((subItem, subIndex) =>
                  renderMenuItem(subItem, subIndex, level + 1)
                )}
              </List>
            </Collapse>
          </Box>
        )}
      </React.Fragment>
    );
  }, [expandedItems, toggleExpanded, onDrawerToggle, isItemActive, getButtonStyles, getIconStyles, getTextStyles]);

  // Разделяем секции на фиксированные и скроллируемые
  const fixedSections = sections.filter(s => s.fixed);
  const scrollableSections = sections.filter(s => !s.fixed);

  // Рендерит одну секцию
  const renderSection = (section: SidebarSection) => (
    <Box key={section.id}>
      {/* Кастомный контент секции */}
      {section.customContent && (
        <Box sx={{ mb: '20px' }}>
          {section.customContent}
        </Box>
      )}

      {/* Заголовок секции */}
      {section.title && (
        <Box sx={{ mb: '20px' }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '18px',
              background: 'linear-gradient(45deg, #ffffff 0%, #e0e0e0 50%, #ffffff 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textAlign: 'center',
              mb: '10px',
            }}
          >
            {section.title}
          </Typography>
        </Box>
      )}

      {/* Элементы секции */}
      {section.items && section.items.length > 0 && (
        <Box sx={section.withMenuSection ? menuSectionStyles : {}}>
          <List sx={{ p: 0 }}>
            {section.items.map((item, index) =>
              renderMenuItem(item, index, 0)
            )}
          </List>
        </Box>
      )}
    </Box>
  );

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} sx={{ color: 'white' }} />
        </Box>
      ) : (
        <>
          {/* Фиксированные секции (не скроллятся) */}
          {fixedSections.length > 0 && (
            <Box
              sx={{
                flexShrink: 0,
                p: {
                  xs: '20px 20px 0 20px',
                  sm: '25px 25px 0 25px',
                  md: '30px 30px 0 30px',
                  lg: '35px 35px 0 35px'
                },
                ...fixedSectionStyles,
              }}
            >
              {fixedSections.map(renderSection)}
            </Box>
          )}

          {/* Скроллируемые секции */}
          {scrollableSections.length > 0 && (
            <SimpleBar
              style={{
                flex: 1,
                minHeight: 0,
                paddingRight: 8,
              }}
              autoHide={false}
            >
              <Box
                sx={{
                  p: {
                    xs: fixedSections.length > 0 ? '0 20px 0 20px' : '20px 20px 0 20px',
                    sm: fixedSections.length > 0 ? '0 25px 0 25px' : '25px 25px 0 25px',
                    md: fixedSections.length > 0 ? '0 30px 0 30px' : '30px 30px 0 30px',
                    lg: fixedSections.length > 0 ? '0 35px 0 35px' : '35px 35px 0 35px'
                  },
                }}
              >
                {scrollableSections.map(renderSection)}
              </Box>
            </SimpleBar>
          )}
        </>
      )}
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{
        width: 0,
        flexShrink: 0,
        ...(!mobileOnly && !collapsed && {
          '@media (min-width: 870px)': {
            width: drawerWidth.sm,
          },
          '@media (min-width: 900px)': {
            width: drawerWidth.md,
          },
          '@media (min-width: 1200px)': {
            width: drawerWidth.lg,
          },
        }),
      }}
    >
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onDrawerToggle}
        ModalProps={{
          keepMounted: false,
        }}
        sx={{
          display: 'block',
          '@media (min-width: 870px)': {
            display: 'none',
          },
          visibility: mobileOpen ? 'visible' : 'hidden',
          '& .MuiDrawer-paper': {
            ...drawerPaperStyles,
            width: 'calc(100vw - 48px)', // Такая же ширина как у GlassContainer (mx: 3 = 24px с каждой стороны)
            left: '24px', // Такой же отступ как у GlassContainer (mx: 3)
            height: 'calc(100dvh - 158px)', // Такая же высота как у GlassContainer (dvh для iOS Safari)
          },
        }}
      >
        {drawer}
      </Drawer>
      {!mobileOnly && !collapsed && (
        <Drawer
          variant="permanent"
          sx={{
            display: 'none',
            '@media (min-width: 870px)': {
              display: 'block',
            },
            '& .MuiDrawer-paper': {
              ...drawerPaperStyles,
              width: drawerWidth.sm,
              '@media (min-width: 900px)': {
                width: drawerWidth.md,
              },
              '@media (min-width: 1200px)': {
                width: drawerWidth.lg,
              },
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      )}
    </Box>
  );
};

export default UniversalSidebar;